import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, HttpStatusCode, InternalAxiosRequestConfig} from "axios";
import {firstValueFrom} from "rxjs";
import {AuthenticationRepository} from "./AuthenticationRepository.ts";
import HTTPConverter from "./converters/HTTPConverter.ts";
import generalErrorConverter from "./converters/GeneralErrorConverter.ts";
import {PipelineRepositoryType} from "./interfaces/PipelineRepositoryType.ts";
import {ActivationChannel} from "../domain/entities/Activation.ts";
import {
    BoardQuery,
    BoardResponse,
    CrmActivationDetail,
    CrmCommand,
    CrmNote,
    HistoryPage,
    NoteFile,
    NotesFilter,
    PipelineId,
    TasksPage,
    TasksQuery,
    TeamMember,
} from "../domain/entities/Pipeline.ts";
import {
    ConversionBoardQuery,
    ConversionBoardResponse,
    ConversionCommand,
    ConversionCtaCommand,
    CrmConversionDetail,
    normalizeConversionBoard,
    normalizeConversionDetail,
} from "../domain/entities/ConversionPipeline.ts";

// Multi-value filters travel as CSV (?market=US,ES), contract §9.5.
const csv = (values?: string[]) => (values?.length ? values.join(',') : undefined)

// History and notes share their shape in both pipelines (Conversion contract §3.3-3.4).
const entityPath = (pipeline: PipelineId) => (pipeline === 'conversion' ? 'conversions' : 'activations')

// Pipelines · Activation endpoints (51trips-colab-api,
// docs/tripbook-pipelines-activation-endpoint.md §3). Admin-only; same Bearer
// Firebase token, 401 retry and GeneralError conversion as ActivationRepository.
export class PipelineRepository implements PipelineRepositoryType {

    client: AxiosInstance

    get serverBaseURL(): string {
        return import.meta.env.VITE_APP_SERVER_URL
    }

    constructor(authenticationRepository: AuthenticationRepository) {
        this.client = axios.create({
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            timeout: 40000,
            validateStatus: (status: number) => status >= 200 && status < 300
        })

        this.client.interceptors.request.use(
            async (
                config: InternalAxiosRequestConfig
            ): Promise<InternalAxiosRequestConfig & { retry: number }> => {
                const token = await firstValueFrom(authenticationRepository.getIDToken())
                config.headers.set('Authorization', `Bearer ${token}`)

                const configWithRetry = config as InternalAxiosRequestConfig & {
                    retry: number
                }
                return { ...config, retry: configWithRetry.retry === 0 ? 0 : 1 }
            }
        )

        this.client.interceptors.response.use(
            (response) => HTTPConverter.fromResponse(response),
            async (err: AxiosError) => {
                const config = err.config as AxiosRequestConfig & { retry: number }
                if (err?.response?.status === HttpStatusCode.Unauthorized && config && config.retry > 0) {
                    config.retry = 0
                    return this.client.request(config)
                }
                throw generalErrorConverter.fromBackendServerError(err)
            }
        )
    }

    getActivationBoard({market, assignee, ...query}: BoardQuery): Promise<BoardResponse> {
        return this.client.get(`${this.serverBaseURL}/crm/pipelines/activation/board`, {
            params: {...query, market: csv(market), assignee: csv(assignee)},
        })
    }

    getActivation(id: string, channel?: ActivationChannel): Promise<CrmActivationDetail> {
        return this.client.get(`${this.serverBaseURL}/crm/activations/${encodeURIComponent(id)}`, {params: {channel}})
    }

    getHistory(pipeline: PipelineId, id: string, limit: number, cursor?: string): Promise<HistoryPage> {
        return this.client.get(`${this.serverBaseURL}/crm/${entityPath(pipeline)}/${encodeURIComponent(id)}/history`, {params: {limit, cursor}})
    }

    getTasks({market, ...query}: TasksQuery, limit: number, cursor?: string): Promise<TasksPage> {
        return this.client.get(`${this.serverBaseURL}/crm/tasks`, {params: {...query, market: csv(market), limit, cursor}})
    }

    sendCommand(id: string, command: CrmCommand): Promise<CrmActivationDetail> {
        return this.client.post(`${this.serverBaseURL}/crm/activations/${encodeURIComponent(id)}/commands`, command)
    }

    getNotes(pipeline: PipelineId, id: string, filter: NotesFilter): Promise<{items: CrmNote[]}> {
        return this.client.get(`${this.serverBaseURL}/crm/${entityPath(pipeline)}/${encodeURIComponent(id)}/notes`, {params: {filter}})
    }

    addNote(pipeline: PipelineId, id: string, text: string, files?: NoteFile[]): Promise<CrmNote> {
        return this.client.post(`${this.serverBaseURL}/crm/${entityPath(pipeline)}/${encodeURIComponent(id)}/notes`, {text, files})
    }

    setNotePinned(pipeline: PipelineId, id: string, noteId: string, pinned: boolean): Promise<CrmNote> {
        return this.client.patch(`${this.serverBaseURL}/crm/${entityPath(pipeline)}/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`, {pinned})
    }

    // Pipelines · Conversion (docs/tripbook-pipelines-conversion-endpoint.md §3-4).
    getConversionBoard({market, assignee, editor, ...query}: ConversionBoardQuery): Promise<ConversionBoardResponse> {
        return this.client.get<ConversionBoardResponse, ConversionBoardResponse>(`${this.serverBaseURL}/crm/pipelines/conversion/board`, {
            params: {...query, market: csv(market), assignee: csv(assignee), editor: csv(editor)},
        }).then(normalizeConversionBoard)
    }

    getConversion(id: string): Promise<CrmConversionDetail> {
        return this.client.get<CrmConversionDetail, CrmConversionDetail>(`${this.serverBaseURL}/crm/conversions/${encodeURIComponent(id)}`)
            .then(normalizeConversionDetail)
    }

    sendConversionCommand(id: string, command: ConversionCommand | ConversionCtaCommand): Promise<CrmConversionDetail> {
        return this.client.post<CrmConversionDetail, CrmConversionDetail>(`${this.serverBaseURL}/crm/conversions/${encodeURIComponent(id)}/commands`, command)
            .then(normalizeConversionDetail)
    }

    getTeam(): Promise<{items: TeamMember[]}> {
        return this.client.get(`${this.serverBaseURL}/crm/team`)
    }
}
